import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

// Function to get URL query parameter
function getQueryParam(name: string): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
}

const developmentApiEndpoint = '/office-api';
const antiforgeryTokenEndpoint = '/EmailTemplate/GetAntiforgeryToken';
const fallbackAntiforgeryHeaderName = 'RequestVerificationToken';

type AntiforgeryToken = {
    requestToken: string;
    headerName: string;
};

const antiforgeryTokens = new Map<string, Promise<AntiforgeryToken>>();

// Function to determine API endpoint based on sender query parameter
function getApiEndpoint(): string {
    if (import.meta.env.DEV) {
        return developmentApiEndpoint;
    }

    const sender = getQueryParam('sender')?.trim().toLowerCase();
    const endpointMap: Record<string, string> = {
        mallorca: 'https://officenextmallorca.foodtoursbalearics.com',
        ibiza: 'https://officenextibiza.foodtoursbalearics.com',
    };

    return sender ? (endpointMap[sender] || '') : '';
}

function isMutation(method?: string): boolean {
    return ['post', 'put', 'patch', 'delete'].includes(method?.toLowerCase() || '');
}

function getAntiforgeryToken(apiEndpoint: string): Promise<AntiforgeryToken> {
    const existingRequest = antiforgeryTokens.get(apiEndpoint);
    if (existingRequest) {
        return existingRequest;
    }

    const tokenRequest = axios.get<Partial<AntiforgeryToken>>(
        antiforgeryTokenEndpoint,
        {
            baseURL: apiEndpoint,
            withCredentials: true,
        }
    ).then(({ data }) => {
        if (typeof data.requestToken !== 'string' || !data.requestToken.trim()) {
            throw new Error('OfficeNext returned an invalid antiforgery token.');
        }

        return {
            requestToken: data.requestToken,
            headerName: typeof data.headerName === 'string' && data.headerName.trim()
                ? data.headerName
                : fallbackAntiforgeryHeaderName,
        };
    }).catch(error => {
        antiforgeryTokens.delete(apiEndpoint);
        throw error;
    });

    antiforgeryTokens.set(apiEndpoint, tokenRequest);
    return tokenRequest;
}

const API_ENDPOINT = getApiEndpoint();
export const axiosInstance = axios.create({
    baseURL: API_ENDPOINT,
    withCredentials: true,
});

// Function to update base URL when query parameters change
export function updateBaseURL() {
    const newEndpoint = getApiEndpoint();
    axiosInstance.defaults.baseURL = newEndpoint;
}

axiosInstance.interceptors.request.use(async function (config) {
    // Update the base URL to reflect the current sender query parameter.
    const currentEndpoint = getApiEndpoint();
    if (!currentEndpoint) {
        return Promise.reject(new Error('The OfficeNext sender is missing or invalid.'));
    }

    config.baseURL = currentEndpoint;
    config.withCredentials = true;

    if (isMutation(config.method)) {
        const antiforgeryToken = await getAntiforgeryToken(currentEndpoint);
        config.headers.set(
            antiforgeryToken.headerName,
            antiforgeryToken.requestToken
        );
    }

    return config;
});

axiosInstance.interceptors.response.use(
    function <T>(res: AxiosResponse<T>) {
        return new Promise((resolve, reject) => {
            return resolve(res);
        });
    },
    (error) => {
        throw {
            ...error,
            message:
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.response?.data?.title ||
                error?.message ||
                error,
        };
    }
);

export const request = {
    async get<T>(url: string, config?: AxiosRequestConfig | undefined) {
        return axiosInstance.get<T>(url, config).then((data) => data.data);
    },
    async post<T>(
        url: string,
        data?: any,
        config?: AxiosRequestConfig | undefined
    ) {
        return axiosInstance.post<T>(url, data, config).then((data) => data.data);
    },
};
